import { useAccount, useMsal } from "@azure/msal-react"
import { ChangeEvent, FC, useMemo, useState } from "react"

import { Dialog } from "src/components/common/dialog"
import { InputField } from "src/components/common/inputfield"
import { Select } from "src/components/common/select"
import { create } from "src/helpers/bem"

import { useTranslation } from "src/hooks/translation"
import { postPermission } from "src/queriesXRM/permission"
import { useBooking } from "src/states/bookingDetail"
import {
  getBookingDetailPermissions,
  mergeBookingDetailPermission,
  mergeBookingDetailPermissions,
  useBookingDetailPermissions,
} from "src/states/bookingDetailPermissions"

import { useBookings } from "src/states/bookings"
import { TranslationMessages } from "src/translations"
import { xRMApiPermission, xRMApiPermissionRole } from "src/types/xRM"

import styles from "./PermissionTableActionsAddUserDialog.module.scss"

const bem = create(styles, "PermissionTableActionsAddUserDialog")

export type PermissionTableActionsAddUserDialogProps = {
  messages: TranslationMessages
}

export const PermissionTableActionsAddUserDialog: FC<
  PermissionTableActionsAddUserDialogProps
> = ({ messages }) => {
  const m = messages.pages.booking.detail.actions.permissions
  const { accounts } = useMsal()
  const { booking } = useBooking()
  const { bookings } = useBookings()
  const account = useAccount(accounts[0])
  const { formatMessage } = useTranslation()
  const { addUser, permissionTableUser, currentSalesorderId } =
    useBookingDetailPermissions()
  const [open, setOpen] = useState<boolean>(false)

  const currentBooking = bookings.find((b) => b.id === booking?.id)

  useMemo(() => {
    setOpen(addUser.open)
  }, [addUser.open])

  let AddUser = {
    open: addUser.open,
    email: addUser.email,
    emailErrorText: addUser?.emailErrorText,
    role: addUser.role,
    roleErrorText: addUser?.roleErrorText,
  }

  let permissionItems =
    permissionTableUser === "MainExhibitor"
      ? [
          {
            key: "Administrator",
            value:
              messages.pages.booking.detail.actions.permissions.table.role.items
                .Administrator,
          },
        ]
      : []

  permissionItems =
    process.env.ENABLE_WEBSHOP === "true"
      ? [
          ...permissionItems,
          {
            key: "BuyOnOwnAccount",
            value:
              messages.pages.booking.detail.actions.permissions.table.role.items
                .BuyOnOwnAccount,
          },
          {
            key: "BuyOnForeignAccount",
            value: formatMessage(
              messages.pages.booking.detail.actions.permissions.table.role.items
                .BuyOnForeignAccount1,
              { company: currentBooking?.exhibitor },
            ),
          },
        ]
      : permissionItems

  const disableAddUserButton =
    addUser.email == "" ||
    addUser.role == null ||
    addUser.emailErrorText !== undefined ||
    addUser.roleErrorText !== undefined

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    AddUser = {
      ...AddUser,
      email: e.target.value,
    }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const handleValidationEmail = (error?: string) => {
    AddUser = {
      ...AddUser,
      emailErrorText: error,
    }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const handleChangeRole = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    AddUser = { ...AddUser, role: e?.target?.value as xRMApiPermissionRole }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const handleValidationRole = (error?: string) => {
    AddUser = { ...AddUser, roleErrorText: error }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const onAddUser = () => {
    const { permissions } = getBookingDetailPermissions()

    if (
      permissions.some(
        (permission) =>
          permission.email === AddUser.email &&
          permission.role === AddUser.role,
      )
    ) {
      mergeBookingDetailPermissions({
        addUser: {
          open: false,
          email: "",
          role: "Administrator",
        },
      })
      setOpen(false)
    } else {
      postPermission({
        salesorderId: currentSalesorderId ?? undefined,
        email: AddUser.email,
        role: AddUser.role,
      }).then((res: xRMApiPermission) => {
        const Permissions = JSON.parse(
          JSON.stringify(permissions),
        ) as xRMApiPermission[]
        const permissionExist = Permissions.some((el) => el.id === res.id)

        if (AddUser.role == "BuyOnForeignAccount") {
          res.emailForeignAccount = account?.idTokenClaims?.email as string
        }

        if (permissionExist) {
          mergeBookingDetailPermissions({
            addUser: {
              open: false,
              email: "",
              role: "Administrator",
            },
          })
          mergeBookingDetailPermission(res.id ?? "", res)
          return
        }
        mergeBookingDetailPermissions({
          addUser: {
            open: false,
            email: "",
            role: "Administrator",
          },
          permissions: [...Permissions, res],
        })
      })
    }
  }

  return (
    <Dialog
      closeOnClick
      open={open}
      title={m.title}
      firstRowButtons={[
        {
          disabled: disableAddUserButton,
          title: m.actions.add.buttons.add,
          variant: "text",
          onClick: onAddUser,
        },
        {
          title: m.actions.add.buttons.cancel,
          variant: "text",
        },
      ]}
      onClose={() =>
        mergeBookingDetailPermissions({
          addUser: {
            open: !addUser.open,
            email: addUser.email,
            role: addUser.role,
          },
        })
      }
    >
      <div className={bem("form")}>
        <div className={bem("form__field")}>
          <InputField
            required
            errorText={addUser.emailErrorText}
            label={m.actions.add.form.email}
            messages={messages}
            name="email"
            sizeVariant="medium"
            type="email"
            value={addUser.email}
            onChange={handleChangeEmail}
            onValidate={handleValidationEmail}
          />
        </div>

        <div className={bem("form__field")}>
          <Select
            required
            errorText={addUser.roleErrorText}
            items={permissionItems}
            label={m.actions.add.form.role}
            messages={messages}
            name="role"
            sizeVariant="medium"
            value={addUser.role}
            onChange={handleChangeRole}
            onValidate={handleValidationRole}
          />
        </div>
      </div>
    </Dialog>
  )
}
